import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import type {ReauthProvider} from '../../api/types';

/**
 * Google/Apple re-authentication for accounts without a password
 * (backend#263 Phase B): the decisions both apps share. Each app draws its
 * own buttons and calls its own provider SDK; which panel the change-email
 * screen shows, how a failure reads and when a proof was refused live here.
 */

/** Providers that can confirm a password-less user, in display order. */
export const REAUTH_PROVIDERS: readonly ReauthProvider[] = ['google', 'apple'];

/** Brand names: never translated (glossary). */
export const REAUTH_PROVIDER_NAMES: Record<ReauthProvider, string> = {
  google: 'Google',
  apple: 'Apple',
};

export type EmailChangeMode =
  /** The usual form: new address and current password. */
  | {kind: 'password'}
  /** "Confirm it's you" with one of these linked providers first. */
  | {kind: 'provider'; providers: ReauthProvider[]}
  /** Phase A: no way to confirm yet, so email a link to set a password. */
  | {kind: 'setPasswordLink'};

/**
 * Which change-email flow to show.
 *
 * Only an explicit `hasUsablePassword === false` leaves the password form:
 * `null` means /me has not said (not loaded, or an older backend). For a
 * password-less account, the providers `reauth_methods` lists; none, or no
 * `reauth_methods` at all (an older backend, or the `social_reauth` flag off
 * server-side), is the set-password link of Phase A.
 */
export const getEmailChangeMode = ({
  hasUsablePassword,
  reauthMethods,
}: {
  hasUsablePassword: boolean | null;
  reauthMethods: readonly string[] | null;
}): EmailChangeMode => {
  if (hasUsablePassword !== false) {
    return {kind: 'password'};
  }
  const providers = REAUTH_PROVIDERS.filter((provider) =>
    reauthMethods?.includes(provider),
  );
  return providers.length
    ? {kind: 'provider', providers}
    : {kind: 'setPasswordLink'};
};

export type ReauthFailure =
  /** The provider account signed in is not the one linked to this user. */
  | {
      kind: 'accountMismatch';
      provider: ReauthProvider;
      /** The linked account's address, from begin's `login_hint`. */
      linkedEmail: string | null;
    }
  /** 503: the provider's keys could not be fetched. */
  | {kind: 'providerUnavailable'; provider: ReauthProvider}
  /**
   * The browser blocked the provider's popup (Apple JS
   * `popup_blocked_by_browser`), typically because it opened after a request
   * rather than inside the press. The challenge is kept, so pressing again
   * opens it inside the gesture.
   */
  | {kind: 'popupBlocked'; provider: ReauthProvider}
  /** 429: begin and complete share 10 attempts an hour. */
  | {kind: 'throttled'}
  /** Anything else: a stale, replayed or invalid token, the network, the SDK. */
  | {kind: 'failed'};

type MaybeApiError =
  | {
      /** Apple JS rejects with `{error: '<code>'}`. */
      error?: unknown;
      statusCode?: number;
      details?: {code?: string | number};
      fields?: Record<string, {code?: string | number}>;
    }
  | null
  | undefined;

/**
 * Read a failed begin/complete (an `ErrorInfo` from `useApiClient`) or a
 * provider SDK's throw. Classified on `details.code` except for 429: a 503
 * arrives as a SERVER_ERROR, which carries no `statusCode`.
 */
export const classifyReauthError = (
  error: unknown,
  provider: ReauthProvider,
  loginHint: string | null,
): ReauthFailure => {
  const info = error as MaybeApiError;
  if (info?.error === 'popup_blocked_by_browser') {
    return {kind: 'popupBlocked', provider};
  }
  if (info?.statusCode === 429) {
    return {kind: 'throttled'};
  }
  const code = info?.details?.code;
  if (code === 'account_mismatch') {
    return {kind: 'accountMismatch', provider, linkedEmail: loginHint};
  }
  if (code === 'provider_unavailable') {
    return {kind: 'providerUnavailable', provider};
  }
  return {kind: 'failed'};
};

export type ReauthText = {
  message: MessageDescriptor;
  values?: Record<string, string>;
};

/** What the confirm step says after a failure. */
export const reauthFailureMessage = (failure: ReauthFailure): ReauthText => {
  switch (failure.kind) {
    case 'accountMismatch':
      if (failure.provider === 'apple') {
        // Apple's address is often a private relay one: naming it would
        // only confuse, so the Apple copy never does.
        return {message: messages.account.reauthAccountMismatchApple};
      }
      return failure.linkedEmail
        ? {
            message: messages.account.reauthAccountMismatchGoogleNamed,
            values: {email: failure.linkedEmail},
          }
        : {message: messages.account.reauthAccountMismatchGoogle};
    case 'providerUnavailable':
      return {
        message: messages.account.reauthProviderUnavailable,
        values: {provider: REAUTH_PROVIDER_NAMES[failure.provider]},
      };
    case 'throttled':
      return {message: messages.account.reauthThrottled};
    case 'popupBlocked':
      return {message: messages.account.reauthPopupBlocked};
    default:
      return {message: messages.account.reauthFailed};
  }
};

/** The status line once a provider confirmed the user. */
export const reauthConfirmedMessage = (
  provider: ReauthProvider,
): ReauthText => ({
  message: messages.account.reauthConfirmed,
  values: {provider: REAUTH_PROVIDER_NAMES[provider]},
});

/**
 * True when the email change refused the proof itself: expired or already
 * spent (`reauth_invalid`) or missing (`reauth_required`), both under
 * `reauth_token`. The app then goes back to the confirm step, keeping the
 * typed address.
 */
export const isReauthRejected = (error: unknown): boolean => {
  const code = (error as MaybeApiError)?.fields?.reauth_token?.code;
  return code === 'reauth_invalid' || code === 'reauth_required';
};

/**
 * True when begin says the flow is not (or no longer) the user's: a password
 * was set meanwhile (`reauth_not_needed`), the provider was unlinked
 * (`provider_not_linked`), or the `social_reauth` flag is off for them (403
 * `reauth_disabled`). The app's /me is out of date: refreshing it moves the
 * screen to the right flow instead of a "try again" that cannot succeed.
 */
export const isStaleModeError = (error: unknown): boolean => {
  const info = error as MaybeApiError;
  const code = info?.details?.code;
  return (
    code === 'reauth_not_needed' ||
    code === 'reauth_disabled' ||
    info?.fields?.provider?.code === 'provider_not_linked'
  );
};
